import React, { useMemo, useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { isPendingIzin } from './utils/izinApproval';
import { isPendingUang, isUangItem } from './utils/uangApproval';

const BottomNav = ({ isEnabled }) => {
  const [trigger, setTrigger] = useState(0);

  useEffect(() => {
    let obPollRunning = false;
    const handleStorageChange = () => setTrigger(t => t + 1);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('badgeUpdate', handleStorageChange);

    // Auto refresh badge tiap 3 detik
    const interval = setInterval(handleStorageChange, 3000);

    // Background polling khusus OB untuk notifikasi tugas baru
    const pollObTasks = async () => {
      if (obPollRunning) return;
      obPollRunning = true;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const userData = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
        const jabatan = userData.jabatan?.toLowerCase();

        if (jabatan === 'office boy') {
          // A. Dapatkan shift jam_masuk dari riwayat_absen atau shift_terpilih
          const getObShiftJam = () => {
            try {
              const today = new Date();
              const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
              const riwayat = JSON.parse(localStorage.getItem('riwayat_absen') || '[]');
              const todayEntry = riwayat.find(item => item.tanggal === todayStr && item.jam_masuk);
              return todayEntry?.jam_masuk || null;
            } catch { return null; }
          };

          const getShiftTerpilih = () => {
            try {
              const stored = localStorage.getItem('shift_terpilih');
              return stored ? JSON.parse(stored) : null;
            } catch { return null; }
          };

          const jamMasukParam = getObShiftJam() || getShiftTerpilih()?.jam_masuk || '';
          const jamParam = jamMasukParam ? `?jam_masuk=${jamMasukParam}` : '';

          // B. Fetch tasks dan reports secara paralel
          const [tasksRes, reportsRes] = await Promise.all([
            fetch(`https://jne-cilacap.com/slip/public/api/ob/tasks${jamParam}`, { signal: controller.signal }),
            fetch('https://jne-cilacap.com/slip/public/api/ob/reports', { signal: controller.signal })
          ]);

          const tasksJson = await tasksRes.json();
          const reportsJson = await reportsRes.json();

          if (tasksJson.status === 'success' && reportsJson.status === 'success') {
            const today = new Date();
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            const currentUserId = String(userData.id);

            // 1. Filter laporan hari ini
            const allTodayReports = reportsJson.data.filter(r => r.updated_at && r.updated_at.startsWith(todayStr));
            const obReportsFromServer = allTodayReports.filter(r => parseInt(r.user_id) === parseInt(currentUserId));

            // 2. Hitung subtask dengan REVISI (ada catatan perbaikan & status Dikonfirmasi, tanpa laporan pending baru)
            const subtasksWithRevision = obReportsFromServer.filter(
              r => r.status === 'Dikonfirmasi' && r.catatan && r.catatan.trim() !== ''
            );
            const revisionCount = subtasksWithRevision.filter(r => {
              const hasPendingForThisSubtask = allTodayReports.some(
                report => parseInt(report.task_id) === parseInt(r.task_id) &&
                  parseInt(report.subtask_index) === parseInt(r.subtask_index) &&
                  report.status !== 'Dikonfirmasi'
              );
              return !hasPendingForThisSubtask;
            }).length;

            // 3. Filter today's extra tasks (task tambahan)
            const extras = [];
            tasksJson.data.forEach(task => {
              if (task.items) {
                task.items.forEach(item => {
                  if (item.updated_at && !item.updated_at.startsWith(todayStr)) return;
                  extras.push({
                    task_id: task.id,
                    title: item.item_name,
                    updated_at: item.updated_at,
                    user_id: item.user_id,
                    id: item.id
                  });
                });
              }
            });
          }
        } else if (jabatan === 'human capital' || jabatan === 'general affair') {
          const res = await fetch('https://jne-cilacap.com/slip/public/api/ob/reports', { signal: controller.signal });
          const json = await res.json();
          if (json.status === 'success') {
            const today = new Date();
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

            const todayReports = json.data.filter(r =>
              r.updated_at && r.updated_at.startsWith(todayStr)
            );

            const totalBadge = todayReports.filter(r =>
              r.status === 'Selesai'
            ).length;

            localStorage.setItem('badge_ob_pending_reports', totalBadge);
            localStorage.setItem('badge_ob', totalBadge);
            window.dispatchEvent(new Event('badgeUpdate'));
          }
        }
      } catch (e) { if (e.name !== 'AbortError') console.error('OB Poll Error:', e); }
      finally { clearTimeout(timeout); obPollRunning = false; }
    };

    pollObTasks();
    const obInterval = setInterval(pollObTasks, 60000); // Cek tiap 1 menit

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('badgeUpdate', handleStorageChange);
      clearInterval(interval);
      clearInterval(obInterval);
    };
  }, []);

  // Hitung jumlah laporan per kategori
  const { jumlahKonfirmasi, badgeAbsen, badgeIzin, badgeUang, badgeLupaAbsen, badgeOb, badgeTitipan } = useMemo(() => {
    try {
      // ... existing logic ...
      const countAbsen = JSON.parse(localStorage.getItem("absen_konfirmasi") || "[]")
        .filter(item =>
          item.absen_id === 18 &&
          item.nama !== null &&
          (item.log === "lapangan" || item.log === "gps error")
        ).length;
      const countLupaAbsen = Array.isArray(JSON.parse(localStorage.getItem("lapor_lupa_absen") || "[]")) ? JSON.parse(localStorage.getItem("lapor_lupa_absen") || "[]").length : 0;
      const ijinKonfirmasi = JSON.parse(localStorage.getItem("ijin_konfirmasi") || "[]");
      const ijinPengajuan = JSON.parse(localStorage.getItem("ijin_pengajuan") || "[]");
      const countIzin =
        (Array.isArray(ijinKonfirmasi)
          ? ijinKonfirmasi.filter(item =>
            item.status === 'Izin' &&
            item.acc != 2 &&
            item.telat != 2 &&
            isPendingIzin(item)
          ).length
          : 0) +
        (Array.isArray(ijinPengajuan)
          ? ijinPengajuan.filter(item =>
            !isUangItem(item) && isPendingIzin(item)
          ).length
          : 0);
      const uangKonfirmasi = JSON.parse(localStorage.getItem("uang_konfirmasiPotongan") || "[]");
      const uangPengajuanKasbon = JSON.parse(localStorage.getItem("uang_pengajuanKasbon") || "[]");
      const uangDariIjin = JSON.parse(localStorage.getItem("ijin_pengajuan") || "[]");
      const countUang =
        (Array.isArray(uangKonfirmasi) ? uangKonfirmasi.filter(isPendingUang).length : 0) +
        (Array.isArray(uangPengajuanKasbon) ? uangPengajuanKasbon.filter(isPendingUang).length : 0) +
        (Array.isArray(uangDariIjin) ? uangDariIjin.filter(isPendingUang).length : 0);

      // 5. OB Tab
      const countOb = parseInt(localStorage.getItem('badge_ob') || '0') || 0;

      // 6. Titipan Pending (Serah Terima Masuk)
      const dataTitipan = JSON.parse(localStorage.getItem('data_titipan') || '[]');
      const dataKaryawan = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const myId = String(dataKaryawan.id);
      const countTitipan = dataTitipan.filter(t =>
        String(t.recipient_id) === myId &&
        String(t.current_holder_id) !== myId &&
        t.status !== 'Selesai'
      ).length;

      // Simpan individu agar Lapor.js bisa baca
      localStorage.setItem('badge_absen', countAbsen);
      localStorage.setItem('badge_lupa_absen', countLupaAbsen);
      localStorage.setItem('badge_izin', countIzin);
      localStorage.setItem('badge_uang', countUang);
      localStorage.setItem('badge_ob_val', countOb); // Avoid collision with badge_ob if needed
      localStorage.setItem('badge_titipan', countTitipan);

      const total = countAbsen + countLupaAbsen + countIzin + countUang;
      localStorage.setItem('badge_lapor_total', total);

      return {
        jumlahKonfirmasi: total,
        badgeAbsen: countAbsen,
        badgeLupaAbsen: countLupaAbsen,
        badgeIzin: countIzin,
        badgeUang: countUang,
        badgeOb: countOb,
        badgeTitipan: countTitipan
      };
    } catch (e) {
      return { jumlahKonfirmasi: 0, badgeAbsen: 0, badgeLupaAbsen: 0, badgeIzin: 0, badgeUang: 0, badgeOb: 0, badgeTitipan: 0 };
    }
  }, [trigger]);

  if (!isEnabled) {
    return null;
  }

  // Cek apakah user saat ini adalah Agen JNE
  const isAgen = (() => {
    try {
      const k = JSON.parse(localStorage.getItem('data_karyawan') || '{}');
      const u = JSON.parse(localStorage.getItem('data_user') || '{}');
      return k.is_agen === true || u.is_agen === true ||
        String(k.role || '').toLowerCase() === 'agen' ||
        String(u.role || '').toLowerCase() === 'agen' ||
        localStorage.getItem('is_agen') === 'true' ||
        window.location.pathname === '/agen' ||
        (Boolean(k.nik) && !String(k.nik).toUpperCase().startsWith('CXP'));
    } catch { return false; }
  })();

  // Jika akun Agen: tampilkan 1 tombol saja: "Login SRM"
  if (isAgen) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '10px',
          left: '10px',
          right: '10px',
          maxWidth: '450px',
          margin: '0 auto',
          background: '#f0f4ff',
          padding: '8px 12px',
          borderRadius: '24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          border: '1px solid #dbeafe',
          zIndex: 1000,
        }}
      >
        <button
          type="button"
          id="btn-login-srm"
          onClick={() => {
            const win = window.open('https://jne-cilacap.com/pesan/public/backend/login', '_blank');
            if (!win) {
              window.location.href = 'https://jne-cilacap.com/pesan/public/backend/login';
            }
          }}
          style={{
            width: '100%',
            height: '46px',
            background: 'linear-gradient(135deg, #4a90e2 0%, #9013fe 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '18px',
            fontSize: '15px',
            fontWeight: 700,
            letterSpacing: '0.4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            boxShadow: '0 3px 12px rgba(144, 19, 254, 0.35)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}
          onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
          onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
          onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <i className="fas fa-sign-in-alt" style={{ fontSize: '16px' }} />
          <span>Login SRM</span>
        </button>
      </div>
    );
  }

  const iconStyle = {
    fontSize: "20px",
    marginBottom: "4px",
  };

  const handleMenuClick = (path) => {
    window.location.href = path;
  };

  // Daftar kategori badge yang akan ditampilkan di Notif navbar
  const badgeItems = [
    { count: badgeAbsen, icon: "fas fa-clock", color: "#3b82f6" },
    { count: badgeLupaAbsen, icon: "fas fa-user-clock", color: "#f59e0b" },
    { count: badgeIzin, icon: "fas fa-calendar-times", color: "#8b5cf6" },
    { count: badgeUang, icon: "fas fa-wallet", color: "#10b981" },
  ].filter(b => b.count > 0);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '10px',
        left: '10px',
        right: '10px',
        background: '#f0f4ff',
        padding: '10px 0',
        borderRadius: '20px',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          ...menuStyle,
          opacity: isEnabled ? 1 : 1,
        }}
        onClick={() => handleMenuClick('/keuangan')}
      >
        <i className="fas fa-wallet" style={iconStyle} />
        <small>Uang</small>
      </div>

      <div
        style={{
          ...menuStyle,
          marginRight: '80px',
          opacity: isEnabled ? 1 : 1,
        }}
        onClick={() => handleMenuClick('/izin')}
      >
        <i className="fas fa-calendar-times" style={iconStyle} />
        <small>Izin</small>
      </div>

      <div
        style={{
          position: 'absolute',
          top: '-25px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, #4a90e2, #9013fe)',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontSize: '14px',
          fontWeight: 'bold',
          cursor: 'pointer',
          zIndex: 1001,
        }}
        onClick={() =>
          handleMenuClick('https://jne-cilacap.com/slip/public/login')
        }
      >
        Login
      </div>

      <div
        style={{
          ...menuStyle,
          opacity: isEnabled ? 1 : 1,
        }}
        onClick={() => handleMenuClick('/absen')}
      >
        <i className="fas fa-clock" style={iconStyle} />
        <small>Absen</small>
      </div>

      {/* === Tombol Notif dengan badge Instagram-style === */}
      <div
        style={{
          ...menuStyle,
          position: "relative",
          opacity: isEnabled ? 1 : 1,
        }}
        onClick={() => handleMenuClick('/lapor')}
      >
        {/* Badge 1 baris — speech bubble dengan kerucut ke bawah mengarah ke icon */}
        {badgeItems.length > 0 && (
          <div
            style={{
              position: "absolute",
              bottom: "calc(100% + 2px)",
              right: "-4px",          // rata kanan, tidak overflow screen
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end", // konten rata kanan
              pointerEvents: "none",
              zIndex: 1002,
            }}
          >
            {/* Baris badge horizontal */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 0,
                backgroundColor: "#ef4444",
                borderRadius: 999,
                padding: "7px 16px",
                boxShadow: "0 4px 14px rgba(239,68,68,0.5)",
                border: "2px solid rgba(255,255,255,0.35)",
              }}
            >
              {badgeItems.map(({ count, icon }, i) => (
                <React.Fragment key={icon}>
                  {i > 0 && (
                    <div style={{
                      width: 1,
                      height: 17,
                      backgroundColor: "rgba(255,255,255,0.4)",
                      margin: "0 10px",
                    }} />
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#fff",
                      fontSize: 13,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    <i className={icon} style={{ fontSize: 12 }} />
                    {count > 99 ? "99+" : count}
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Kerucut ke bawah → ke icon Notif, offset ke kanan agar mengarah ke icon */}
            <div
              style={{
                marginRight: "10px",
                width: 0,
                height: 0,
                borderLeft: "9px solid transparent",
                borderRight: "9px solid transparent",
                borderTop: "10px solid #ef4444",
                marginTop: -1,
              }}
            />
          </div>
        )}

        {/* Icon Notif */}
        <i className="fas fa-bell" style={iconStyle} />
        <small>Notif</small>
      </div>
    </div>
  );
};

const menuStyle = {
  color: '#4a90e2',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  cursor: 'pointer',
};

const iconStyle = {
  fontSize: '20px',
  marginBottom: '5px',
};

export default BottomNav;
